import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Input, MessageBar, MessageBarBody, Option, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Tooltip } from "@fluentui/react-components";
import { ArrowDownloadRegular, CopyRegular, DismissRegular, SearchRegular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../AppContext";
import { Module } from "../../models/module";
import { RoleDTO } from "../../models/role";
import { getRoleOverview } from "../../services/usersService";

type Props = {
  open: boolean;
  onClose: () => void;
};

type RoleRow = {
  role: string;
  moduleName: string;
  groupName: string;
  name: string;
  email: string;
  organizationName: string;
};

const ALL = "all";
const ROLE_ORDER = ["Kontoadmin", "Ramavtalsansvarig", "Behörighetsansvarig", "Kommunanvändare"];
// Rendering thousands of rows freezes the page; the list is capped but copy/CSV always use every matching row
const MAX_RENDERED_ROWS = 300;

const csvCell = (value: string) => `"${(value ?? "").replace(/"/g, '""')}"`;

const UsersByRoleDialog = (props: Props) => {
  const { user } = useAppContext();

  const [moduleId, setModuleId] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState<string>(ALL);
  const [organizationFilter, setOrganizationFilter] = useState<string>(ALL);
  const [search, setSearch] = useState<string>("");
  const [rows, setRows] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const isGeneral = user?.roles.some((r: RoleDTO) => r.roleType === 'General') ?? false;

  // General users can look at any ramavtal, everyone else only at the ones they administer
  const modules: Module[] = useMemo(() => {
    if (!user) return [];
    if (isGeneral) {
      return user.modules.filter((m: Module) => m.moduleType === 'Tendering');
    }
    const administered: Module[] = user.assignments
      .filter((a: any) => a.role.roleName === 'Ramavtalsansvarig' || a.role.roleName === 'Kontoadmin')
      .map((a: any) => a.module);
    return administered.filter((m, i) => administered.findIndex(x => x.moduleID === m.moduleID) === i);
  }, [user, isGeneral]);

  useEffect(() => {
    if (props.open) {
      setModuleId(modules.length === 1 ? modules[0].moduleID.toString() : "");
      setRoleFilter(ALL);
      setOrganizationFilter(ALL);
      setSearch("");
      setRows([]);
      setErrorMessage(null);
      setCopied(false);
    }
  }, [props.open, modules]);

  useEffect(() => {
    if (!props.open || !moduleId) return;

    const selectedModules = moduleId === ALL ? modules : modules.filter(m => m.moduleID.toString() === moduleId);

    const load = async () => {
      setLoading(true);
      setErrorMessage(null);
      setCopied(false);
      try {
        // One call: the server scopes it to what this user is allowed to see
        const overview = await getRoleOverview(selectedModules.map(m => m.moduleID));
        setRows(overview.map(o => ({
          role: o.roleName,
          moduleName: o.moduleName,
          groupName: o.userGroupName ?? "",
          name: `${o.firstName ?? ""} ${o.lastName ?? ""}`.trim(),
          email: o.email,
          organizationName: o.organizationName ?? ""
        })));
      } catch (error) {
        console.error('Error fetching users by role:', error);
        setRows([]);
        setErrorMessage('Användarna kunde inte hämtas.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [props.open, moduleId]);

  const organizations = useMemo(
    () => Array.from(new Set(rows.map(r => r.organizationName).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [rows]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter(r => roleFilter === ALL || r.role === roleFilter)
      .filter(r => organizationFilter === ALL || r.organizationName === organizationFilter)
      .filter(r => !term || `${r.name} ${r.email} ${r.groupName} ${r.moduleName} ${r.organizationName}`.toLowerCase().includes(term))
      .sort((a, b) =>
        ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role)
        || a.organizationName.localeCompare(b.organizationName)
        || a.moduleName.localeCompare(b.moduleName)
        || a.groupName.localeCompare(b.groupName)
        || a.email.localeCompare(b.email));
  }, [rows, roleFilter, organizationFilter, search]);

  const renderedRows = useMemo(() => visibleRows.slice(0, MAX_RENDERED_ROWS), [visibleRows]);

  const uniqueEmails = useMemo(
    () => Array.from(new Set(visibleRows.map(r => r.email.toLowerCase()))),
    [visibleRows]);

  const showModuleColumn = moduleId === ALL;
  const showOrganizationColumn = isGeneral;
  const columnCount = 4 + (showModuleColumn ? 1 : 0) + (showOrganizationColumn ? 1 : 0);
  const copyDisabled = loading || uniqueEmails.length === 0;
  const downloadDisabled = loading || visibleRows.length === 0;

  const handleCopyEmails = async () => {
    const text = uniqueEmails.join("; ");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for browsers/contexts without the async clipboard API
      const area = document.createElement("textarea");
      area.value = text;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      document.body.removeChild(area);
    }
    setCopied(true);
  };

  const handleDownloadCsv = () => {
    const header = ["Roll", "Organisation", "Ramavtal", "Grupp", "Namn", "E-post"];
    const lines = [header, ...visibleRows.map(r => [r.role, r.organizationName, r.moduleName, r.groupName, r.name, r.email])]
      .map(line => line.map(csvCell).join(";"));
    // BOM + semicolon so Excel opens it correctly with Swedish settings
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `behorigheter-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="alert"
    >
      <DialogSurface style={{ maxWidth: 1250, width: "92vw" }}>
        <DialogBody>
          <DialogTitle
            action={
              <Tooltip content="Stäng" relationship="label">
                <Button
                  appearance="subtle"
                  shape="circular"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={props.onClose}
                />
              </Tooltip>
            }
          >
            Användare per roll
          </DialogTitle>
          <DialogContent>
            {errorMessage && (
              <MessageBar intent="error" className="mb-3">
                <MessageBarBody>{errorMessage}</MessageBarBody>
              </MessageBar>
            )}
            <div className="flex gap-3 flex-wrap items-end mb-3">
              <Field label="Ramavtal:" style={{ minWidth: 260 }}>
                <Dropdown
                  placeholder="Välj ramavtal"
                  value={moduleId === ALL ? "Alla ramavtal" : (modules.find(m => m.moduleID.toString() === moduleId)?.moduleName ?? "")}
                  onOptionSelect={(_e, data) => setModuleId(data.optionValue ?? "")}
                  positioning={{ autoSize: false }}
                  listbox={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                >
                  {modules.length > 1 && <Option value={ALL}>Alla ramavtal</Option>}
                  {modules.map(m => (
                    <Option key={m.moduleID} value={m.moduleID.toString()}>{m.moduleName}</Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Roll:" style={{ minWidth: 200 }}>
                <Dropdown
                  value={roleFilter === ALL ? "Alla roller" : roleFilter}
                  onOptionSelect={(_e, data) => setRoleFilter(data.optionValue ?? ALL)}
                >
                  <Option value={ALL}>Alla roller</Option>
                  {ROLE_ORDER.map(r => <Option key={r} value={r}>{r}</Option>)}
                </Dropdown>
              </Field>
              {showOrganizationColumn && (
                <Field label="Organisation:" style={{ minWidth: 220 }}>
                  <Dropdown
                    value={organizationFilter === ALL ? "Alla organisationer" : organizationFilter}
                    onOptionSelect={(_e, data) => setOrganizationFilter(data.optionValue ?? ALL)}
                    positioning={{ autoSize: false }}
                    listbox={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                  >
                    <Option value={ALL}>Alla organisationer</Option>
                    {organizations.map(o => <Option key={o} value={o}>{o}</Option>)}
                  </Dropdown>
                </Field>
              )}
              <Field label="Sök:" style={{ minWidth: 220 }}>
                <Input
                  contentBefore={<SearchRegular />}
                  placeholder="Namn, e-post eller grupp"
                  value={search}
                  onChange={(_e, data) => setSearch(data.value)}
                />
              </Field>
            </div>

            {!moduleId ? (
              <Text>Välj ett ramavtal för att se vilka användare som har vilken roll.</Text>
            ) : loading ? (
              <div className="flex justify-center py-8"><Spinner label="Hämtar användare..." /></div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Text size={200}>
                    {visibleRows.length} behörigheter, {uniqueEmails.length} unika användare
                    {visibleRows.length > MAX_RENDERED_ROWS && ` – visar de första ${MAX_RENDERED_ROWS}, kopiering och CSV innehåller alla`}
                  </Text>
                  {copied && <Text size={200} style={{ color: 'var(--colorPaletteGreenForeground1)' }}>E-postadresserna är kopierade.</Text>}
                </div>
                <div style={{ maxHeight: 380, overflowY: "auto" }}>
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        {/* The table uses a fixed layout: give the short columns explicit widths so the email column gets the rest */}
                        <TableHeaderCell style={{ width: 150 }}>Roll</TableHeaderCell>
                        {showOrganizationColumn && <TableHeaderCell style={{ width: 150 }}>Organisation</TableHeaderCell>}
                        {showModuleColumn && <TableHeaderCell style={{ width: 210 }}>Ramavtal</TableHeaderCell>}
                        <TableHeaderCell style={{ width: 110 }}>Grupp</TableHeaderCell>
                        <TableHeaderCell style={{ width: 170 }}>Namn</TableHeaderCell>
                        <TableHeaderCell>E-post</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {renderedRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columnCount}>
                            <Text>Inga användare hittades.</Text>
                          </TableCell>
                        </TableRow>
                      ) : renderedRows.map((r, i) => (
                        <TableRow key={`${r.role}-${r.moduleName}-${r.groupName}-${r.email}-${i}`}>
                          <TableCell>{r.role}</TableCell>
                          {showOrganizationColumn && <TableCell>{r.organizationName || "–"}</TableCell>}
                          {showModuleColumn && <TableCell>{r.moduleName}</TableCell>}
                          <TableCell>{r.groupName || "–"}</TableCell>
                          <TableCell>{r.name}</TableCell>
                          {/* Long addresses wrap instead of being cut off */}
                          <TableCell style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{r.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Tooltip
              relationship="description"
              content={copyDisabled
                ? "Det finns inga e-postadresser att kopiera. Välj ett ramavtal och en roll som har användare."
                : "Kopierar e-postadresserna för användarna i listan till urklipp, åtskilda med semikolon. Varje adress tas bara med en gång. Respekterar valt ramavtal, roll och sökfilter."}
            >
              <Button icon={<CopyRegular />} onClick={handleCopyEmails} disabledFocusable={copyDisabled}>
                Kopiera e-postadresser
              </Button>
            </Tooltip>
            <Tooltip
              relationship="description"
              content={downloadDisabled
                ? "Det finns inget att ladda ner. Välj ett ramavtal och en roll som har användare."
                : "Laddar ner listan som en CSV-fil (roll, organisation, ramavtal, grupp, namn och e-post) som kan öppnas i Excel. Respekterar valt ramavtal, roll och sökfilter."}
            >
              <Button icon={<ArrowDownloadRegular />} onClick={handleDownloadCsv} disabledFocusable={downloadDisabled}>
                Ladda ner CSV
              </Button>
            </Tooltip>
            <Tooltip relationship="description" content="Stänger fönstret. Ingenting ändras.">
              <Button appearance="primary" onClick={props.onClose}>Stäng</Button>
            </Tooltip>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UsersByRoleDialog;
